import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProviderCalendar from '../components/ProviderCalendar';
import BookingCompletionFlow from '../components/BookingCompletionFlow';
import ProviderAvailability from '../components/ProviderAvailability';
import FavoriteButton from '../components/FavoriteButton';
import { Heart } from 'lucide-react';
import { useFees } from '../context/FeeContext';

// Import refactored dashboard components
import {
  DashboardStats,
  FinancialOverview,
  ServicesList,
  JobsList,
  JobBidsModal,
  AcceptQuoteModal,
  JobChatModal,
  BidInquiryModal,
  EditServiceModal
} from '../components/dashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, onLogout, onUserUpdate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { feeRates, calculateProviderPayout } = useFees();
  
  // Redirect admin users to Admin Dashboard
  const effectiveRole = user?.active_role || user?.role;
  useEffect(() => {
    if (effectiveRole === 'admin') {
      navigate('/admin', { replace: true });
    }
  }, [effectiveRole, navigate]);
  
  // Core data state
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    if (!user) return;
    if (effectiveRole !== 'client') { setFavorites([]); return; }
    axios.get(`${API}/favorites`)
      .then((res) => setFavorites(res.data || []))
      .catch(() => setFavorites([]));
  }, [user, effectiveRole]);

  const handleUnfavorite = (providerId, isFav) => {
    if (!isFav) setFavorites((prev) => prev.filter((f) => f.provider_id !== providerId));
  };

  // Modal states
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  
  // Job bids modal
  const [selectedJobForBids, setSelectedJobForBids] = useState(null);
  const [jobBids, setJobBids] = useState([]);
  const [loadingBids, setLoadingBids] = useState(false);
  const [processingBid, setProcessingBid] = useState(null);
  
  // Edit service modal
  const [showEditServiceModal, setShowEditServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingServiceImages, setEditingServiceImages] = useState([]);
  const [uploadingEditImages, setUploadingEditImages] = useState(false);
  const [savingService, setSavingService] = useState(false);
  
  // Accept quote modal
  const [showAcceptQuoteModal, setShowAcceptQuoteModal] = useState(false);
  const [acceptingBooking, setAcceptingBooking] = useState(null);
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [projectEndDate, setProjectEndDate] = useState('');
  const [projectEndTime, setProjectEndTime] = useState('17:00');
  const [blockCalendar, setBlockCalendar] = useState(false);

  // Job chat modal
  const [showJobChatModal, setShowJobChatModal] = useState(false);
  const [selectedJobConversation, setSelectedJobConversation] = useState(null);
  const [jobMessages, setJobMessages] = useState([]);
  const [newJobMessage, setNewJobMessage] = useState('');
  const [sendingJobMessage, setSendingJobMessage] = useState(false);
  const [loadingJobMessages, setLoadingJobMessages] = useState(false);

  // Bid inquiry modal
  const [showBidInquiryModal, setShowBidInquiryModal] = useState(false);
  const [selectedBidForInquiry, setSelectedBidForInquiry] = useState(null);
  const [bidInquiryMessage, setBidInquiryMessage] = useState('');
  const [sendingBidInquiry, setSendingBidInquiry] = useState(false);


  // ==================== Data Fetching ====================
  
  useEffect(() => {
    fetchDashboardData();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedJobForBids) {
      fetchJobBids(selectedJobForBids.id);
    }
  }, [selectedJobForBids]);

  useEffect(() => {
    if (showJobChatModal && selectedJobConversation) {
      fetchJobMessages(selectedJobConversation.job_id);
    }
  }, [showJobChatModal, selectedJobConversation]);

  // Handle URL parameters for deep linking
  useEffect(() => {
    const handleDeepLinks = async () => {
      if (loading) return;
      
      const viewBidsJobId = searchParams.get('view_bids');
      const bookingId = searchParams.get('booking');
      const jobChatId = searchParams.get('job_chat');
      
      if (viewBidsJobId) {
        const job = jobs.find(j => j.id === viewBidsJobId);
        if (job) setSelectedJobForBids(job);
        searchParams.delete('view_bids');
        setSearchParams(searchParams, { replace: true });
      }
      
      if (bookingId) {
        const booking = bookings.find(b => b.id === bookingId);
        if (booking) setSelectedBooking(booking);
        searchParams.delete('booking');
        setSearchParams(searchParams, { replace: true });
      }
      
      if (jobChatId) {
        try {
          const response = await axios.get(`${API}/jobs/${jobChatId}`);
          if (response.data) {
            setSelectedJobConversation({
              job_id: jobChatId,
              job_title: response.data.title
            });
            setShowJobChatModal(true);
          }
        } catch (error) {
          console.error('Error fetching job for chat:', error);
        }
        searchParams.delete('job_chat');
        setSearchParams(searchParams, { replace: true });
      }
    };

    handleDeepLinks();
  }, [loading, jobs, bookings, searchParams, setSearchParams]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/bookings`)
      ]);

      setStats(statsRes.data);
      setBookings(bookingsRes.data);

      if (effectiveRole === 'provider') {
        const servicesRes = await axios.get(`${API}/services/my`);
        setServices(servicesRes.data);
      } else {
        const jobsRes = await axios.get(`${API}/jobs/my`);
        setJobs(jobsRes.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchJobBids = async (jobId) => {
    setLoadingBids(true);
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/bids`);
      setJobBids(response.data);
    } catch (error) {
      console.error('Error fetching job bids:', error);
      setJobBids([]);
    } finally {
      setLoadingBids(false);
    }
  };

  const fetchJobMessages = async (jobId) => {
    setLoadingJobMessages(true);
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/conversation`);
      setJobMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching job messages:', error);
      setJobMessages([]);
    } finally {
      setLoadingJobMessages(false);
    }
  };

  // ==================== Handlers ====================

  const handleAcceptBid = async (bidId) => {
    if (!window.confirm('Are you sure you want to accept this bid? Other bids will be automatically rejected.')) return;
    
    setProcessingBid(bidId);
    try {
      await axios.put(`${API}/bids/${bidId}/accept`);
      fetchJobBids(selectedJobForBids.id);
      fetchDashboardData();
      alert('Bid accepted! The provider has been notified.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to accept bid');
    } finally {
      setProcessingBid(null);
    }
  };

  const handleRejectBid = async (bidId) => {
    if (!window.confirm('Are you sure you want to reject this bid?')) return;
    
    setProcessingBid(bidId);
    try {
      await axios.put(`${API}/bids/${bidId}/reject`);
      fetchJobBids(selectedJobForBids.id);
      alert('Bid rejected.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reject bid');
    } finally {
      setProcessingBid(null);
    }
  };

  const handleBookingAction = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}`, { status });
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating booking:', error);
    }
  };

  const openAcceptQuoteModal = (booking) => {
    setAcceptingBooking(booking);
    setQuoteAmount(booking.price || '');
    setQuoteNotes('');
    setProjectEndDate('');
    setProjectEndTime('17:00');
    setBlockCalendar(false);
    setShowAcceptQuoteModal(true);
  };

  const handleAcceptWithQuote = async () => {
    if (!acceptingBooking || !quoteAmount || parseFloat(quoteAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    // For project bookings, require end date
    if (acceptingBooking.price_unit === 'project' && !projectEndDate) {
      alert('Please set the project end date');
      return;
    }

    setSubmittingQuote(true);
    try {
      const params = new URLSearchParams();
      params.append('final_amount', quoteAmount);
      if (quoteNotes) params.append('notes', quoteNotes);
      
      // Add project timeline params for project bookings
      if (acceptingBooking.price_unit === 'project') {
        params.append('project_end_date', projectEndDate);
        if (projectEndTime) params.append('project_end_time', projectEndTime);
        params.append('block_calendar', blockCalendar);
      }

      await axios.put(`${API}/bookings/${acceptingBooking.id}/accept-with-quote?${params.toString()}`);
      
      setShowAcceptQuoteModal(false);
      setAcceptingBooking(null);
      setQuoteAmount('');
      setQuoteNotes('');
      setProjectEndDate('');
      setProjectEndTime('17:00');
      setBlockCalendar(false);
      fetchDashboardData();
      alert('Booking accepted successfully!');
    } catch (error) {
      console.error('Error accepting booking:', error);
      alert(error.response?.data?.detail || 'Failed to accept booking');
    } finally {
      setSubmittingQuote(false);
    }
  };

  const calculateFees = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    const providerCalc = calculateProviderPayout(numAmount);
    return {
      platformFee: providerCalc.providerFee.toFixed(2),
      providerPayout: providerCalc.providerPayout.toFixed(2)
    };
  };

  const sendJobMessage = async () => {
    if (!newJobMessage.trim() || !selectedJobConversation) return;
    
    setSendingJobMessage(true);
    try {
      await axios.post(`${API}/jobs/${selectedJobConversation.job_id}/conversation`, {
        message: newJobMessage.trim()
      });
      setNewJobMessage('');
      fetchJobMessages(selectedJobConversation.job_id);
    } catch (error) {
      console.error('Error sending message:', error);
      alert(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingJobMessage(false);
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert(error.response?.data?.detail || 'Failed to delete booking');
    }
  };

  const handleSendBidInquiry = async () => {
    if (!bidInquiryMessage.trim() || !selectedBidForInquiry) return;
    
    setSendingBidInquiry(true);
    try {
      await axios.post(`${API}/bids/${selectedBidForInquiry.id}/inquiry`, {
        message: bidInquiryMessage.trim()
      });
      setShowBidInquiryModal(false);
      setBidInquiryMessage('');
      setSelectedBidForInquiry(null);
      alert('Your inquiry has been sent to the bidder!');
    } catch (error) {
      console.error('Error sending bid inquiry:', error);
      alert(error.response?.data?.detail || 'Failed to send inquiry');
    } finally {
      setSendingBidInquiry(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/services/${serviceId}`);
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting service:', error);
      alert(error.response?.data?.detail || 'Failed to delete service');
    }
  };

  const openEditServiceModal = (service) => {
    setEditingService({
      ...service,
      skills_required: service.skills_required || [],
      images: service.images || []
    });
    setEditingServiceImages(service.images || []);
    setShowEditServiceModal(true);
  };

  const handleEditServiceImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (editingServiceImages.length + files.length > 10) {
      alert('Maximum 10 images allowed');
      return;
    }

    setUploadingEditImages(true);

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Each file must be less than 5MB');
        continue;
      }

      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        alert('Only JPEG, PNG, and WebP images are allowed');
        continue;
      }

      try {
        const uploadData = new FormData();
        uploadData.append('file', file);

        const token = localStorage.getItem('token');
        const response = await axios.post(`${API}/upload/service-image`, uploadData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data && response.data.url) {
          setEditingServiceImages(prev => [...prev, response.data.url]);
          setEditingService(prev => ({
            ...prev,
            images: [...(prev.images || []), response.data.url]
          }));
        }
      } catch (error) {
        console.error('Image upload error:', error);
        alert(`Failed to upload image: ${error.response?.data?.detail || error.message}`);
      }
    }

    setUploadingEditImages(false);
  };

  const handleRemoveEditServiceImage = (index) => {
    setEditingServiceImages(prev => prev.filter((_, i) => i !== index));
    setEditingService(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index)
    }));
  };

  const handleUpdateService = async (e) => {
    e.preventDefault();
    if (!editingService) return;
    
    setSavingService(true);
    try {
      await axios.put(`${API}/services/${editingService.id}`, {
        title: editingService.title,
        description: editingService.description,
        category: editingService.category,
        price: parseFloat(editingService.price),
        price_unit: editingService.price_unit,
        currency: editingService.currency || 'PHP',
        location: editingService.location,
        images: editingServiceImages,
        skills_required: editingService.skills_required || []
      });
      setShowEditServiceModal(false);
      setEditingService(null);
      setEditingServiceImages([]);
      fetchDashboardData();
      alert('Service updated successfully!');
    } catch (error) {
      console.error('Error updating service:', error);
      alert(error.response?.data?.detail || 'Failed to update service');
    } finally {
      setSavingService(false);
    }
  };

  // ==================== Helper Functions ====================

  const formatBookingDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatBookingTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      const [hours, minutes] = timeStr.split(':');
      const h = parseInt(hours);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const hour12 = h % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  // ==================== Render ====================

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-semibold text-gray-600">{t('dashboard.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ID Verification Rejected Banner */}
        {user?.id_verification_status === 'rejected' && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3" data-testid="id-rejected-banner">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-red-800 font-semibold text-sm">ID Verification Rejected</h3>
              <p className="text-red-600 text-sm mt-0.5">
                Your identity verification has been rejected. You cannot post jobs or create services until your ID is re-verified. 
                {user?.kyc_rejection_reasons?.length > 0 && (
                  <span className="font-medium"> Reason: {user.kyc_rejection_reasons[user.kyc_rejection_reasons.length - 1]?.reason}</span>
                )}
              </p>
              <Link 
                to="/account-settings" 
                className="inline-block mt-2 text-sm font-semibold text-red-700 hover:text-red-900 underline"
                data-testid="resubmit-id-link"
              >
                Re-submit your ID documents &rarr;
              </Link>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900" data-testid="dashboard-title">
            {t('dashboard.welcomeBack', { name: user?.name })}
          </h1>
        </div>

        {/* Stats Cards */}
        <DashboardStats stats={stats} effectiveRole={effectiveRole} />

        {/* Provider Calendar */}
        {effectiveRole === 'provider' && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {t('dashboard.mySchedule')}
              </h2>
              <button
                onClick={() => setShowAvailabilityModal(true)}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full font-medium hover:bg-gray-200 transition flex items-center gap-2"
                data-testid="manage-availability-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {t('dashboard.manageAvailability')}
              </button>
            </div>
            <ProviderCalendar providerId={user.id} isOwnCalendar={true} />
          </div>
        )}

        {/* Financial Overview */}
        <FinancialOverview 
          user={user} 
          stats={stats} 
          bookings={bookings} 
          effectiveRole={effectiveRole} 
        />

        {/* Quick Links to My Services and My Bookings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* My Services Quick Link - Provider only */}
          {effectiveRole === 'provider' && (
            <Link
              to="/my-services"
              className="bg-white p-6 rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-md transition group"
              data-testid="quick-link-my-services"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{t('dashboard.myServices')}</h3>
                    <p className="text-sm text-gray-500">{services.length} active service{services.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          )}

          {/* My Bookings Quick Link - Provider only */}
          {effectiveRole === 'provider' && (
            <Link
              to="/my-bookings"
              className="bg-white p-6 rounded-2xl border border-gray-100 hover:border-purple-200 hover:shadow-md transition group"
              data-testid="quick-link-my-bookings"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{t('dashboard.myBookings')}</h3>
                    <p className="text-sm text-gray-500">
                      {bookings.filter(b => b.status === 'pending').length} pending, {bookings.filter(b => b.status === 'accepted').length} active
                    </p>
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-purple-600 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          )}
        </div>

        {/* My Favorite Providers - clients only */}
        {effectiveRole === 'client' && (
        <div className="mb-8" data-testid="dashboard-favorites-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500 fill-red-500" />
              {t('favorites.sectionTitle')}
            </h2>
            {favorites.length > 0 && (
              <Link to="/favorites" className="text-sm font-semibold text-orange-600 hover:text-orange-700" data-testid="dashboard-favorites-viewall">
                {t('favorites.viewAll')} ({favorites.length}) →
              </Link>
            )}
          </div>
          {favorites.length === 0 ? (
            <div className="bg-white p-6 rounded-2xl border border-gray-100 text-center" data-testid="dashboard-favorites-empty">
              <p className="text-sm text-gray-500">
                {t('favorites.dashEmpty')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {favorites.slice(0, 6).map((p) => (
                <div key={p.provider_id} className="bg-white p-4 rounded-2xl border border-gray-100 hover:shadow-md transition relative" data-testid={`dashboard-favorite-${p.provider_id}`}>
                  <div className="absolute top-2 right-2">
                    <FavoriteButton providerId={p.provider_id} user={user} initialFavorited={true} onChange={handleUnfavorite} variant="floating" size="sm" />
                  </div>
                  <Link to={`/provider/${p.provider_id}`} className="flex items-center gap-3">
                    {p.avatar ? (
                      <img src={p.avatar.startsWith('http') ? p.avatar : `${BACKEND_URL}${p.avatar}`} alt={p.name} className="w-12 h-12 rounded-full object-cover border-2 border-gray-100" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center text-white font-semibold">
                        {p.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{p.name}</p>
                      {p.rating > 0 && (
                        <p className="text-xs text-gray-500">★ {p.rating.toFixed(1)} ({p.total_reviews})</p>
                      )}
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Pending Bookings Alert */}
        {bookings.filter(b => b.status === 'completion_requested').length > 0 && (
          <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 mb-8" data-testid="pending-completion-alert">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-purple-900">
                    {bookings.filter(b => b.status === 'completion_requested').length} booking{bookings.filter(b => b.status === 'completion_requested').length !== 1 ? 's' : ''} awaiting completion review
                  </p>
                  <p className="text-sm text-purple-700">{t('dashboard.confirmCompletedWork')}</p>
                </div>
              </div>
              <Link
                to="/my-bookings?filter=completion_requested"
                className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-purple-700 transition"
              >
                {t('dashboard.reviewNow')}
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Booking Completion Modal */}
      {selectedBooking && (
        <BookingCompletionFlow
          booking={selectedBooking}
          user={user}
          userRole={effectiveRole}
          onClose={() => setSelectedBooking(null)}
          onUpdate={fetchDashboardData}
        />
      )}

      {/* Provider Availability Modal */}
      {showAvailabilityModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <ProviderAvailability onClose={() => setShowAvailabilityModal(false)} />
          </div>
        </div>
      )}

      {/* Job Bids Modal */}
      <JobBidsModal
        job={selectedJobForBids}
        bids={jobBids}
        loading={loadingBids}
        processingBid={processingBid}
        onClose={() => {
          setSelectedJobForBids(null);
          setJobBids([]);
        }}
        onAcceptBid={handleAcceptBid}
        onRejectBid={handleRejectBid}
        onMessageProvider={(bid) => {
          setSelectedJobConversation({
            job_id: selectedJobForBids.id,
            job_title: selectedJobForBids.title,
            provider_id: bid.provider_id,
            provider_name: bid.provider_name
          });
          setSelectedJobForBids(null);
          setShowJobChatModal(true);
        }}
        onAskQuestion={(bid) => {
          setSelectedBidForInquiry({
            ...bid,
            job_title: selectedJobForBids.title,
            job_id: selectedJobForBids.id
          });
          setShowBidInquiryModal(true);
        }}
      />

      {/* Edit Service Modal */}
      <EditServiceModal
        service={editingService}
        images={editingServiceImages}
        categories={categories}
        uploading={uploadingEditImages}
        saving={savingService}
        user={user}
        onServiceChange={setEditingService}
        onImageUpload={handleEditServiceImageUpload}
        onImageRemove={handleRemoveEditServiceImage}
        onSave={handleUpdateService}
        onClose={() => {
          setShowEditServiceModal(false);
          setEditingService(null);
          setEditingServiceImages([]);
        }}
      />

      {/* Accept Quote Modal */}
      <AcceptQuoteModal
        booking={acceptingBooking}
        quoteAmount={quoteAmount}
        quoteNotes={quoteNotes}
        projectEndDate={projectEndDate}
        projectEndTime={projectEndTime}
        blockCalendar={blockCalendar}
        submitting={submittingQuote}
        onQuoteAmountChange={setQuoteAmount}
        onQuoteNotesChange={setQuoteNotes}
        onProjectEndDateChange={setProjectEndDate}
        onProjectEndTimeChange={setProjectEndTime}
        onBlockCalendarChange={setBlockCalendar}
        onSubmit={handleAcceptWithQuote}
        onClose={() => {
          setShowAcceptQuoteModal(false);
          setAcceptingBooking(null);
          setQuoteAmount('');
          setQuoteNotes('');
          setProjectEndDate('');
          setProjectEndTime('17:00');
          setBlockCalendar(false);
        }}
        calculateFees={calculateFees}
      />

      {/* Job Chat Modal */}
      <JobChatModal
        conversation={selectedJobConversation}
        messages={jobMessages}
        newMessage={newJobMessage}
        loading={loadingJobMessages}
        sending={sendingJobMessage}
        userId={user?.id}
        onMessageChange={setNewJobMessage}
        onSend={sendJobMessage}
        onClose={() => {
          setShowJobChatModal(false);
          setSelectedJobConversation(null);
          setJobMessages([]);
          setNewJobMessage('');
        }}
      />

      {/* Bid Inquiry Modal */}
      <BidInquiryModal
        bid={selectedBidForInquiry}
        message={bidInquiryMessage}
        sending={sendingBidInquiry}
        onMessageChange={setBidInquiryMessage}
        onSend={handleSendBidInquiry}
        onClose={() => {
          setShowBidInquiryModal(false);
          setSelectedBidForInquiry(null);
          setBidInquiryMessage('');
        }}
      />

      <Footer />
    </div>
  );
};

export default Dashboard;
